<template>
    <div
        class="betSlip _singleTicket notranslate"
        data-bet-slip-scroll-boundary
        data-testid="single-bet-slip"
    >
        <JackpotWidget v-if="isShowJackpotWidget" v-bind:showCloseButton="false" />
        <div class="betSlip-area">
            <div v-bind:class="{ _stakePadShow: !isShowStakePad }" class="betSlip-scrollContent">
                <EuroSingleTicket
                    v-bind:key="betSlip.key"
                    v-bind:isMixParlay="false"
                    v-bind:singleTicket="betSlip"
                    v-on:refresh="refresh"
                    v-on:refreshTicket="refresh(false, true)"
                    v-on:removeTicket="removeTicket"
                />
                <Message
                    v-if="betSlip.message.key"
                    status="error"
                    v-bind:messageKey="betSlip.message.key"
                    v-bind:messageData="betSlip.message.data"
                    data-testid="betslip-error"
                />
                <SwitchVoucherRedeem
                    v-if="isVoucherAvailable"
                    gaClassPrefix="SingleSlip"
                    v-bind:isRedeemVoucher="isRedeemVoucher"
                    v-on:toggleRedeemVoucher="setIsRedeemVoucher"
                />
                <div class="ticket-maxPayout">
                    <span>{{ i18n.t(betSlipMaxPayoutTranslationKey) }}:</span>
                    <span class="notranslate ticket-amount">{{ maxPayout }}</span>
                </div>
                <div class="ticket-inputArea">
                    <PlaceBet
                        v-if="!isRedeemVoucher"
                        v-bind:stake="stake"
                        v-bind:isDisableBtn="isDisablePlaceBet"
                        v-bind:balance="balance"
                        v-bind:minBet="betSlip.minBet"
                        v-bind:price="betSlip.price"
                        v-bind:isRedeemVoucher="isRedeemVoucher"
                        v-on:placeBet="placeBet()"
                        v-on:changeStake="stake = $event"
                    />
                    <StakePad
                        v-if="!isRedeemVoucher"
                        v-bind:stake="stake"
                        v-bind:balance="balance"
                        v-bind:minBet="betSlip.minBet"
                        v-bind:maxBet="betSlip.maxBet"
                        v-bind:currency="customerInfo.currency"
                        v-bind:price="betSlip.price"
                        v-on:changeStake="stake = $event"
                    />
                    <PlaceVoucherBet
                        v-if="isRedeemVoucher"
                        v-bind:voucherList="voucherList"
                        v-bind:isDisableBtn="isDisablePlaceBet"
                        v-on:updateSelectedVoucher="updateSelectedVoucher"
                        v-on:placeVoucherBet="placeBet()"
                    />
                </div>
                <SwitchAcceptAnyOddsValue
                    v-if="isShowAcceptAnyOdds"
                    class="betSlip-acceptAnyOdds"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
    import SwitchAcceptAnyOddsValue from '@/components/SwitchAcceptAnyOddsValue.vue';
    import EuroSingleTicket from '@/components/betSlip/EuroSingleTicket.vue';
    import PlaceBet from '@/components/betSlip/PlaceBet.vue';
    import PlaceVoucherBet from '@/components/betSlip/PlaceVoucherBet.vue';
    import StakePad from '@/components/betSlip/StakePad.vue';
    import Message from '@/components/common/Message.vue';
    import JackpotWidget from '@/components/jackpot/JackpotWidget.vue';
    import SwitchVoucherRedeem from '@/components/voucher/SwitchVoucherRedeem.vue';
    import type { IVoucherDisplay } from '@/interface/voucher';
    import { isBalanceLessMinBet } from '@/libraries/betSlip/stake';
    import { useJackpotDisplay } from '@/libraries/composables/useJackpotDisplay';
    import { i18n } from '@/libraries/i18n';
    import { enableScrolling } from '@/libraries/utils/dom';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useSingleBetSlipStore } from '@/stores/betSlip/singleBetSlipStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';

    const { isShowJackpotWidget } = useJackpotDisplay();
    const { isShowStakePad } = storeToRefs(useBetSlipStore());

    const ticks = ref(0);

    const { customerInfo, balance } = storeToRefs(useAccountStore());
    const {
        betSlip, stake, isPlacingBet, selectedVoucher, isValidStake, isValidBetSetting, maxPayout, voucherList, isRedeemVoucher,
    } = storeToRefs(useSingleBetSlipStore());
    const { openTicket, placeBet, setIsRedeemVoucher, removeTicket, updateTicket } = useSingleBetSlipStore();
    const { isEuroSite } = storeToRefs(useFeatureToggleStore());

    const isShowAcceptAnyOdds = computed(() => !customerInfo.value.isAcceptAnyOdds);
    const isVoucherAvailable = computed(() => voucherList.value.some(x => x.isAvailable));
    const betSlipMaxPayoutTranslationKey = computed(() => (isEuroSite.value ? 'payout_EURO' : 'maxPayout'));

    const isDisablePlaceBet = computed(() => {
        if (!isValidBetSetting.value) return true;
        if (isPlacingBet.value) return true;
        if (isRedeemVoucher.value) return !isValidStake.value;
        if (isBalanceLessMinBet(balance.value, betSlip.value.minBet, betSlip.value.price)) return true;

        if (balance.value === null) return false;

        return Math.floor(balance.value) <= 0;
    });

    async function tick() {
        ticks.value++;
        if (ticks.value >= 10) {
            await refresh(false, false);
        }
    }

    async function refresh(isFirstTime: boolean, force: boolean) {
        ticks.value = 0;
        if (force) {
            await realRefresh(isFirstTime);
        }
    }

    async function realRefresh(isFirstTime: boolean) {
        await openTicket(isFirstTime);
    }

    function updateSelectedVoucher(voucher: IVoucherDisplay | null) {
        selectedVoucher.value = voucher;
    }

    const { setIsShowStakePad } = useBetSlipStore();
    setIsShowStakePad(true);

    onMounted(async () => {
        window.eventEmitter.on('tick', tick);
        await refresh(true, true);
    });

    watch(betSlip, async () => {
        await refresh(true, true);
    });

    watch(voucherList, (newVal, oldVal) => {
        const newAvailableVouchers = newVal.filter(x => x.isAvailable);
        const oldAvailableVouchers = oldVal.filter(x => x.isAvailable);

        // if all the voucher become not available, show message to user
        if (oldAvailableVouchers.length > 0 && newAvailableVouchers.length === 0) {
            updateTicket({
                key: betSlip.value.key,
                messageKey: 'betSlip_errorMessage_voucher_oddsChanged',
            });
        }
    });

    onUnmounted(() => {
        window.eventEmitter.off('tick', tick);
        enableScrolling();
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/betSlip";
    @import "@/scss/components/ticket";
</style>
